import { useReactToPrint } from "react-to-print";
import React, { useRef, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import CommonFooter from "../../core/common/footer/commonFooter";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { all_routes } from "../../Router/all_routes";
import BillingServices from "../../services/BillingServices";
import useAsync from "../../hooks/useAsync";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { BRAND_INFO } from "../../utils/constants";

const Invoicedetails = () => {
  const { id } = useParams();
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");

  useEffect(() => {
    if (action === "download") {
      handleDownloadPDF();
    } else if (action === "print") {
      reactToPrintFn();
    }
  }, [action, reactToPrintFn]);

  const handleDownloadPDF = async () => {
    const content = contentRef.current;
    if (!content) return;

    const canvas = await html2canvas(content);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 30;

    pdf.addImage(
      imgData,
      "PNG",
      imgX,
      imgY,
      imgWidth * ratio,
      imgHeight * ratio
    );
    pdf.save(`invoice-${bill?.billNumber || "download"}.pdf`);
    alert("PDF downloaded successfully");
  };

  const { data: bill, loading } = useAsync(() => {
    const fetchBillDetails = async () => {
      if (!id) {
        return null;
      }
      const response = await BillingServices.getBillingById(id);
      return response;
    };
    return fetchBillDetails();
  }, [id]);

  if (loading || !bill) {
    return <div>Loading...</div>;
  }

  const route = all_routes;

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Invoice Details</h4>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <Link
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadPDF();
                  }}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Download PDF"
                >
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                </Link>
              </li>
              <li>
                <Link
                  onClick={(e) => {
                    e.preventDefault();
                    reactToPrintFn();
                  }}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Print"
                >
                  <i data-feather="printer" className="feather-rotate-ccw" />
                </Link>
              </li>
              <li>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Collapse"
                  id="collapse-header"
                >
                  <i data-feather="chevron-up" className="feather-chevron-up" />
                </Link>
              </li>
            </ul>
            <div className="page-btn">
              <Link to={route.onlineorder} className="btn btn-primary">
                <i data-feather="arrow-left" className="me-2" />
                Back to Invoices
              </Link>
            </div>
          </div>
          <div className="card" ref={contentRef}>
            <div className="card-body">
              <div className="row justify-content-between align-items-center border-bottom mb-3">
                <div className="col-md-6">
                  <div className="mb-2">
                    <ImageWithBasePath
                      src="assets/img/logo.png"
                      className="printer-logo"
                      alt="logo"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="text-end mb-3">
                    <h5 className="text-gray mb-1">
                      Invoice No{" "}
                      <span className="text-primary">#{bill.billNumber}</span>
                    </h5>
                    <p className="mb-1 fw-medium">
                      Created Date:{" "}
                      <span className="text-dark">
                        {new Date(bill.createdAt).toLocaleDateString()}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="row border-bottom mb-3">
                <div className="col-md-5">
                  <h4 className="mb-1">{BRAND_INFO.name}</h4>
                  <p className="mb-1">{BRAND_INFO.address}</p>
                  <p className="mb-1">
                    <span className="text-dark fw-mediumk">Website: </span>
                    <span className="text-dark fw-mediumk">
                      {BRAND_INFO.website}
                    </span>
                  </p>
                  <p>
                    <span className="text-dark fw-medium">GSTIN: </span>
                    <span className="text-dark fw-medium">
                      {BRAND_INFO.gstin}
                    </span>
                  </p>
                </div>
                <div className="col-md-4"></div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <div>
                      <h4 className="mb-1">Invoice For</h4>
                      <p className="mb-1">
                        <span className="text-dark fw-medium">
                          Customer Name:{" "}
                        </span>
                        <span className="text-dark fw-medium">
                          {bill.customer.name}
                        </span>
                      </p>
                      <p>
                        <span className="text-dark fw-medium">
                          Customer Number:{" "}
                        </span>
                        <span className="text-dark fw-medium">
                          {bill.customer.phone}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <p className="fw-medium">
                  Invoice For:{" "}
                  <span className="text-dark fw-medium">Services</span>
                </p>
                <div className="table-responsive mb-3">
                  <table className="table">
                    <thead className="thead-light">
                      <tr>
                        <th>Services</th>
                        <th className="text-end">Gross Amount</th>
                        <th className="text-end">Qty</th>
                        <th className="text-end">Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bill.services.map((service, index) => (
                        <tr key={index}>
                          <td>
                            <h6>{service.service.name}</h6>
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            ₹{service.price.toFixed(2)}
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            {service.quantity}
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            ₹{(service.price * service.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="row border-bottom mb-3">
                <div className="col-md-5 ms-auto mb-3">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3">
                    <p className="mb-0">Gross Total</p>
                    <p className="text-dark fw-medium mb-2">
                      ₹{bill.grossTotal.toFixed(2)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                    <p className="mb-0">CGST(2.5%)</p>
                    <p className="text-dark fw-medium mb-2">
                      ₹{bill.cgstAmount.toFixed(2)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                    <p className="mb-0">SGST(2.5%)</p>
                    <p className="text-dark fw-medium mb-2">
                      ₹{bill.sgstAmount.toFixed(2)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3">
                    <p className="mb-0">Net Total</p>
                    <p className="text-dark fw-medium mb-2">
                      ₹{bill.afterDiscount.toFixed(2)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3">
                    <p className="mb-0">Discount</p>
                    <p className="text-dark fw-medium mb-2">
                      ₹{bill.discount.toFixed(2)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                    <h5>Grand Total Amount</h5>
                    <h5>₹{bill.netPayable.toFixed(2)}</h5>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-dark mb-1">
                  Payment Made Via {bill.paymentMethod}
                </p>
                <div className="d-flex justify-content-center align-items-center">
                  <p className="fs-12 mb-0 me-3">
                    Status: <span className="text-dark">{bill.status}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center align-items-center mb-4">
            <button
              onClick={() => reactToPrintFn()}
              className="btn btn-primary d-flex justify-content-center align-items-center me-2"
            >
              Print Invoice
              <i className="ti ti-printer" />
            </button>
            <button
              onClick={() => handleDownloadPDF()}
              className="btn btn-secondary d-flex justify-content-center align-items-center"
            >
              Download PDF
              <i className="ti ti-download" />
            </button>
          </div>
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default Invoicedetails;
